import { t as tr } from '@hana/i18n';
import './CostRequests.i18n';
import { useState } from 'react';
import { IconCreditCard, IconPrinter } from '@tabler/icons-react';
import { useToast } from '@hana/ui';
import type { Voucher } from './types';
import { REQ_GROUPS, REQ_STATUS, groupCount, requestNote, stepChips, type ReqGroup } from './requests';
import { f1 } from './view';
import c from './cost.module.css';
import { useT } from '@hana/i18n';
import s from './requests.module.css';
import './finance.i18n';

// Mint-Cost › tab Đề nghị thanh toán: nhóm lọc + bảng 7 cột (CW 1240). Chỉ đọc; nút ghi báo toast.

const COLS = [
  { header: 'Phiếu · người lập', w: 170 },
  { header: 'Đối tác · job', w: 236 },
  { header: 'Tổng', w: 90, right: true },
  { header: 'BIL · ABS · OBH', w: 250 },
  { header: 'Bước duyệt', w: 240 },
  { header: 'Cờ', w: 150 },
  { header: 'Trạng thái', w: 104 },
] as const;
const TOTAL = COLS.reduce((a, x) => a + x.w, 0);
const PAY_TIP = { BNK: 'Người trả BNK · Công ty trả', EMP: 'Người trả EMP · Nhân viên trả' } as const;

export function CostRequests({ vouchers, q }: { vouchers: readonly Voucher[]; q: string }) {
  const toast = useToast();
  const tx = useT();
  const [grp, setGrp] = useState<ReqGroup>('Tất cả');
  const needle = q.trim().toLowerCase();
  const shown = vouchers.filter((v) => (grp === 'Tất cả' || v.group === grp) && (!needle || `${v.no} ${v.partner} ${v.job} ${v.maker}`.toLowerCase().includes(needle)));

  return (
    <>
      <div className={s.grp} role="group" aria-label="Nhóm chi phí">
        {REQ_GROUPS.map((g) => (
          <button key={g} type="button" aria-pressed={grp === g} onClick={() => setGrp(g)}>
            {g} <span>{groupCount(vouchers, g)}</span>
          </button>
        ))}
      </div>
      <section className={c.card} aria-label="Đề nghị thanh toán">
        <table className={c.table} aria-rowcount={shown.length + 1}>
          <colgroup>
            {COLS.map((x) => (
              <col key={x.header} style={{ width: `${((x.w / TOTAL) * 100).toFixed(3)}%` }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {COLS.map((x) => (
                <th key={x.header} scope="col" className={'right' in x ? c.right : undefined}>
                  {x.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((v, i) => {
              const st = REQ_STATUS[v.status];
              const note = requestNote(v);
              return (
                <tr key={v.no} aria-rowindex={i + 2} onClick={() => toast({ msg: 'Chi tiết đề nghị chưa mở ở bản này' })}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <b className={c.no}>{v.no}</b>
                      <span className={`${s.pay} ${s[`pay_${v.payer}`]}`} title={PAY_TIP[v.payer]} aria-label={PAY_TIP[v.payer]}>
                        {v.payer}
                      </span>
                    </div>
                    <div className={c.sub}>
                      {v.date} · {v.maker}
                    </div>
                  </td>
                  <td>
                    <b className={c.b}>{v.partner}</b>
                    <div className={c.sub}>{v.job}</div>
                  </td>
                  <td className={c.right}>
                    <b className={c.b}>{f1(v.total)}</b>
                  </td>
                  <td>
                    <div className={s.split}>
                      <i className={s.sBil} style={{ flex: `${Math.max(v.withInvoice, 0.01)} 1 0` }} />
                      <i className={s.sAbs} style={{ flex: `${Math.max(v.noInvoice, 0.01)} 1 0` }} />
                      <i className={s.sObh} style={{ flex: `${Math.max(v.onBehalf, 0.01)} 1 0` }} />
                    </div>
                    <div className={c.sub}>
                      BIL {f1(v.withInvoice)} · ABS {f1(v.noInvoice)} · OBH {f1(v.onBehalf)}
                    </div>
                  </td>
                  <td>
                    <div className={s.steps}>
                      {stepChips(v).map((p) => (
                        <span key={p.key} className={`${s.step} ${s[`step_${p.tone}`]}`} title={p.tip}>
                          {p.label}
                        </span>
                      ))}
                    </div>
                    {note && <div className={c.sub}>{note}</div>}
                  </td>
                  <td>
                    {v.partnerRisk && <span className={`${s.flag} ${s.f_err}`}>{tx('fn.rqRisk')}</span>}
                    {v.autoApproved && <span className={`${s.flag} ${s.f_ok}`}>{tx('fn.rqAuto')}</span>}
                    {v.duplicate && <span className={`${s.flag} ${s.f_warn}`}>{tx('fn.rqDup')}</span>}
                    {v.urgent && <span className={`${s.flag} ${s.f_err}`}>{tx('fn.rqUrgent')}</span>}
                  </td>
                  <td>
                    <span className={`${c.st} ${st.tone === 'blue' ? s.st_blue : c[`st_${st.tone}`]}`}>
                      <span className={c.dot} aria-hidden="true" />
                      {st.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className={s.foot}>
          <span>{tx('fn.mTr')}</span>
          <span className={s.acts}>
            <button type="button" className={s.act} onClick={() => toast({ msg: 'In tổng hợp chưa mở ở bản này' })}>
              <IconPrinter size={16} aria-hidden="true" />
              {tr('creq.1')}
            </button>
            <button type="button" className={`${s.act} ${s.actLink}`} onClick={() => toast({ msg: 'Đợt chi chưa mở ở bản này' })}>
              <IconCreditCard size={16} aria-hidden="true" />
              {tr('creq.2')}
            </button>
          </span>
        </div>
      </section>
    </>
  );
}
